/**
 * Multi-unit Cooling System Summary display (#1154 / #996) — descriptor-driven
 * per-instance rows via Story 4.6 migration.
 *
 * Story 4.6 (#997) re-points this shim at the descriptor Summary source
 * (`coolingSystemDescriptorSummary.ts`) so the Summary surface flows through
 * the shared engine at byte-identical parity with the pre-refactor bespoke
 * output (captured golden masters are the oracle). The section-agnostic
 * helpers (`resolveSummaryCoolingSystemConfigurations`,
 * `countFilledCoolingSystemSummaryFields*`) stay bespoke; only the row-set +
 * per-unit value + per-unit title accessors delegate to the descriptor source.
 */

import {
  COOLING_EDITOR_SHARED_FIELD_KEYS,
  COOLING_PER_INSTANCE_EDITOR_FIELD_KEYS,
  COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS,
} from '../request/fields/requestFieldRegistry';
import {
  coolingSystemDescriptorUnitRowValue,
  getCoolingSystemDescriptorSummaryDefinitions,
  getCoolingSystemDescriptorUnitTitle,
} from '../request/sections/coolingSystemDescriptorSummary';
import { countSubsectionFields } from '../utils/subsectionHeaderMetrics';

import type { SummaryCardRow } from './summaryCardSourceTypes';
import type { CoolingSystemConfiguration } from '../types/coolingSystem';

export {
  COOLING_EDITOR_SHARED_FIELD_KEYS as COOLING_SHARED_REQUEST_FIELD_KEYS,
  COOLING_PER_INSTANCE_EDITOR_FIELD_KEYS as COOLING_PER_INSTANCE_FIELD_KEYS,
};

/**
 * Full ordered per-unit Cooling System Summary row set. Delegates to the
 * descriptor-driven Summary source (`coolingSystemDescriptorSummary.ts`).
 */
export function getPerCoolingSystemSummaryDefinitions(): SummaryCardRow[] {
  return getCoolingSystemDescriptorSummaryDefinitions();
}

/**
 * Cooling has NO request-level shared fields (per registry
 * `COOLING_EDITOR_SHARED_FIELD_KEYS = []` — #997 Bug 9 moved both former
 * shared fields per-instance). Kept as a stable empty export so callers that
 * imported the shared getter compile unchanged.
 */
export function getCoolingSharedSummaryDefinitions(): SummaryCardRow[] {
  return [];
}

export function resolveSummaryCoolingSystemConfigurations(input: {
  sessionUnits?: CoolingSystemConfiguration[] | null;
  draftUnits?: CoolingSystemConfiguration[] | null;
  persistedUnits?: CoolingSystemConfiguration[] | null;
  /** #997 C3: true when edited this session — an empty edited array is authoritative (no delete-all resurrection). */
  edited?: boolean;
}): CoolingSystemConfiguration[] {
  const { sessionUnits, draftUnits, persistedUnits, edited } = input;
  if (sessionUnits && sessionUnits.length > 0) {
    return sessionUnits;
  }
  if (edited) {
    return draftUnits ?? [];
  }
  if (draftUnits && draftUnits.length > 0) {
    return draftUnits;
  }
  if (persistedUnits && persistedUnits.length > 0) {
    return persistedUnits;
  }
  return [];
}

/**
 * Per-unit Summary value — delegates to the descriptor-driven Summary source.
 */
export function coolingSystemSummaryUnitRowValue(
  unit: CoolingSystemConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  return coolingSystemDescriptorUnitRowValue(unit, dataField);
}

/**
 * Per-unit Summary card title — `Existing/New — <name>` (fallback
 * `Existing/New Cooling System N`). Delegates to the descriptor-driven title
 * function so the Summary shim and the descriptor source stay aligned
 * (mirrors LU / PD / SU shim additions).
 */
export function getCoolingSystemSummaryUnitTitle(
  unit: CoolingSystemConfiguration,
  index: number,
): string {
  return getCoolingSystemDescriptorUnitTitle(unit, index);
}

const COOLING_PER_INSTANCE_COUNT_FIELDS =
  COOLING_SYSTEM_EDITOR_SUBSECTION_FIELDS.filter(
    field => !COOLING_EDITOR_SHARED_FIELD_KEYS.includes(field.key as never),
  );

export function countFilledCoolingSystemSummaryFields(
  unit: CoolingSystemConfiguration,
  rows?: readonly SummaryCardRow[],
): number {
  const fieldKeys = new Set(
    (rows ?? getPerCoolingSystemSummaryDefinitions()).map(row => row.dataField),
  );
  const fields = COOLING_PER_INSTANCE_COUNT_FIELDS.filter(field =>
    fieldKeys.has(field.key),
  );
  return countSubsectionFields(
    fields,
    unit as unknown as Record<string, unknown>,
  ).filled;
}

export function countFilledCoolingSystemSummaryFieldsForUnits(
  units: readonly CoolingSystemConfiguration[],
  rows: readonly SummaryCardRow[],
): number {
  return units.reduce(
    (sum, unit) => sum + countFilledCoolingSystemSummaryFields(unit, rows),
    0,
  );
}
